import React, {
	memo,
	useContext,
	useEffect,
	useMemo,
	useRef,
	Fragment,
	type ContextType,
} from 'react';

import { ContextPanel } from '@atlaskit/editor-core/ContextPanel';

import {
	useIsObjectSidebarShown,
	usePanelWidth,
	usePanel,
	MIN_PANEL_WIDTH,
	CONTENT_RIGHT_PADDING,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { isMigratedToPanelSystem } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import {
	APPROVALS_PANE_ID,
	getApprovalsPaneContextKey,
	syncApprovalsPaneContextOwnership,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import {
	APPROVAL_ACTIVITY_PANE_ID,
	getApprovalActivityPaneContextKey,
	syncApprovalActivityPaneContextOwnership,
} from '@confluence/approvals/entry-points/approvalActivityPaneController.ts';
import { isApprovalActivityEnabled } from '@confluence/approvals/entry-points/isApprovalActivityEnabled.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

export const EditorContextPanelView: React.MemoExoticComponent<() => React.JSX.Element> = memo(
	(): React.JSX.Element => {
		const isObjectSidebarShown = useIsObjectSidebarShown();
		const panelWidth = usePanelWidth();
		const panel = usePanel();

		const { item: contextPanelItem, editorAPI } = useContext(
			EditorContextPanelContext,
		) as ContextType<typeof EditorContextPanelContext>;

		const { contentId, contentStatus } = useApprovals();
		const rightSidebarContext = useRightSidebarContext();
		const currentRightSidebarPanelId = rightSidebarContext.getCurrent()?.panelId;
		const isSidebarApprovalsPaneOpen = currentRightSidebarPanelId === APPROVALS_PANE_ID;
		const isSidebarApprovalActivityPaneOpen =
			currentRightSidebarPanelId === APPROVAL_ACTIVITY_PANE_ID;
		const approvalsPaneContextKey = getApprovalsPaneContextKey({ contentId, contentStatus });
		const isApprovalActivityEnabledForContext = useMemo(() => isApprovalActivityEnabled(), []);

		const rightSidebarContextRef = useRef(rightSidebarContext);
		rightSidebarContextRef.current = rightSidebarContext;

		useEffect(() => {
			syncApprovalsPaneContextOwnership({
				isApprovalsExperimentEnabled: expValEquals('confluence_approvals_eap', 'isEnabled', true),
				isSidebarApprovalsPaneOpen,
				contextKey: approvalsPaneContextKey,
				closeApprovalsPane: () => rightSidebarContextRef.current.close(APPROVALS_PANE_ID),
			});
		}, [approvalsPaneContextKey, isSidebarApprovalsPaneOpen]);

		useEffect(() => {
			if (!isApprovalActivityEnabledForContext) {
				return;
			}

			syncApprovalActivityPaneContextOwnership({
				isApprovalActivityEnabled: true,
				isSidebarApprovalActivityPaneOpen,
				contextKey: fg('cc_approvals_activity_log_edit_mode')
					? getApprovalActivityPaneContextKey({ contentId, contentStatus })
					: '',
				closeApprovalActivityPane: () =>
					rightSidebarContextRef.current.close(APPROVAL_ACTIVITY_PANE_ID),
			});
		}, [
			contentId,
			contentStatus,
			isApprovalActivityEnabledForContext,
			isSidebarApprovalActivityPaneOpen,
		]);

		// Previously, the pattern was to set state in EditorContextPanelContext in order to render components here.
		// Now, we render components directly in EditorContextPanelView to avoid bugs like https://hello.jira.atlassian.cloud/browse/DISCO-4183
		//
		// When the panel system flag is on, comments/details/jira panels render via navigation-system Panel in
		// PageLayout.tsx. Extension panels (ToC, etc.) always render here in ContextPanel.
		if (
			isObjectSidebarShown &&
			!(fg('ads-panel-system-confluence-object-sidebar') && isMigratedToPanelSystem(panel?.id))
		) {
			const isResizablePanel = !!panel?.isResizable;
			const isInjectedAIOrExtensionPanel =
				(panel?.id === 'proactive-ai-panel' ||
					(panel?.id.startsWith && panel?.id.startsWith('extensions-panel')) ||
					panel?.id === 'editor-element-insert-sidebar-panel') &&
				fg('platform_editor_ai_object_sidebar_injection');

			// Width the editor's `<ContextPanel>` should reserve. `panelWidth` is
			// encoded as `visibleWidth + CONTENT_RIGHT_PADDING` for resizable
			// panels (see `setVisiblePanelWidth`); subtract to get the visible
			// width. Clamp to `MIN_PANEL_WIDTH` so we never under-reserve.
			let editorContextPanelCustomWidth;
			if (isResizablePanel) {
				editorContextPanelCustomWidth = Math.max(
					panelWidth - CONTENT_RIGHT_PADDING,
					MIN_PANEL_WIDTH,
				);
			} else if (isInjectedAIOrExtensionPanel) {
				editorContextPanelCustomWidth = panelWidth || MIN_PANEL_WIDTH;
			} else {
				editorContextPanelCustomWidth = MIN_PANEL_WIDTH;
			}

			return (
				<ContextPanel
					visible
					editorAPI={editorAPI ?? undefined}
					hasPadding={false}
					customWidth={editorContextPanelCustomWidth}
				>
					<ObjectSidebarPanel />
				</ContextPanel>
			);
		}

		let component;
		let hasPadding;
		let customWidth;

		if (contextPanelItem) {
			component = contextPanelItem.component;
			hasPadding = contextPanelItem.hasPadding;
			customWidth = contextPanelItem.customWidth;
		}

		return (
			<ContextPanel
				visible={!!contextPanelItem}
				editorAPI={editorAPI ?? undefined}
				hasPadding={hasPadding}
				customWidth={customWidth}
			>
				<Fragment>{component}</Fragment>
			</ContextPanel>
		);
	},
);
